import { Auth } from 'aws-amplify';
import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';

import rootStore from '@/store/RootStore';
import logger from '@/utils/logger';
import { SocketEvent } from '@/types/socket-events.types';

const clientId = uuidv4();
let stationSocket = null;
let lastMessageReceivedDateTime = null;
let socketReconnectionInterval = null;
const SOCKET_RECONNECTION_INTERVAL_MILLIS = 1000 * 60; // 60 seconds;

export const initStationSocket = async stationId => {
  try {
    if (socketReconnectionInterval) {
      clearInterval(socketReconnectionInterval);
    }
    lastMessageReceivedDateTime = null;

    socketReconnectionInterval = setInterval(async () => {
      const now = new Date().getTime();
      const diff = now - lastMessageReceivedDateTime;
      if (diff > SOCKET_RECONNECTION_INTERVAL_MILLIS) {
        logger('socket reconnection interval reached. Reconnecting...');
        await initStationSocket(stationId);
      }
    }, SOCKET_RECONNECTION_INTERVAL_MILLIS);

    const user = await Auth.currentAuthenticatedUser();
    const token = user.signInUserSession.idToken.jwtToken;

    closeSocket();
    stationSocket = new WebSocket(
      `${process.env.VITE_BASE_WEBSOCKET_URL}/v1/?stationId=${stationId}&Authorization=${token}&clientId=${clientId}`,
    );

    stationSocket.onopen = function () {
      logger('socket connected');
    };

    stationSocket.onerror = function (e) {
      logger('error on socket');
      Sentry.captureException(new Error(`socket connection failed - ${e}`));
    };

    stationSocket.onclose = async function () {
      logger('socket closed');
    };

    stationSocket.onmessage = function (event) {
      lastMessageReceivedDateTime = new Date().getTime();
      handleEvent(event);
    };
  } catch (e) {
    logger('Failed to initialize socket' + e);
    Sentry.captureException(new Error(`failed to initialize socket - ${e}`));
  }
};

export const closeSocket = () => {
  if (stationSocket) {
    stationSocket.close();
    stationSocket = null;
  }
};

export const getClientId = () => {
  return clientId;
};

const handleEvent = event => {
  const data: SocketEvent = JSON.parse(event.data);
  switch (data.eventName) {
    case 'deviceUpdate':
      rootStore.stationStore.updateDeviceFromServer(data.payload);
      rootStore.stationRiskStore.handleDeviceUpdateEvent(data.payload);
      break;
    case 'kpiUpdate':
      rootStore.stationStore.updateKPISFromServer(data.payload);
      break;
    case 'alertRecordCreate':
      rootStore.alertStore.addAlertFromServer(data.payload);
      rootStore.stationRiskStore.handleAlertCreatedEvent(data.payload);
      break;
    case 'alertRecordUpdate':
      rootStore.alertStore.updateAlertFromServer(data.payload);
      rootStore.investigationStore.updateAlertFromServer(data.payload);
      rootStore.stationRiskStore.handleAlertUpdatedEvent(data.payload);
      break;
    case 'dataExportResponse':
      rootStore.investigationStore.handleExportEvent(data.payload);
      break;
    case 'proactiveEngineStateUpdate':
      rootStore.proactiveEngineStore.updateProactiveEngineStateFromServer(data.payload);
      break;
    case 'engineStatusUpdate':
      rootStore.proactiveEngineStore.updateProactiveEngineStateFromEngineStatus(data.payload);
      break;
    case 'periodicReportGenerationOnPublish':
      rootStore.reportStore.getReports(
        rootStore.reportStore.pagination.page,
        rootStore.reportStore.pagination.pageSize,
        data.payload.stationId,
      );
      break;
  }
};
